import { PageController } from '@client/components/_core/layout/PageController';
import { Tabs } from '@client/components/_core/navigation/Tabs';
import { VOrgSelect } from '@client/components/_shared/forms/view-form/VOrgSelect';
import { VReadOnly } from '@client/components/_shared/forms/view-form/VReadOnly';
import { VTextArea } from '@client/components/_shared/forms/view-form/VTextArea';
import { VTextInput } from '@client/components/_shared/forms/view-form/VTextInput';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { ComplianceAuditTemplateTypeTag } from '@client/components/compliance-audit-templates/ComplianceAuditTemplateTypeTag';
import { complianceAuditTemplateFormStore } from '@client/components/compliance-audit-templates/store';
import { confirmDelete, KebabMenu } from '@client/components/compliance-audit-templates/structure/KebabMenu';
import { VersionSelect } from '@client/components/compliance-audit-templates/VersionSelect';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { useTitleCacheStorage } from '@client/providers/TitleCacheProvider';
import { trpc } from '@client/trpc/client';
import type { ShareableRouteContext } from '@client/types/router';
import { createFileRoute, retainSearchParams } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Alert, Card, Form, Input, Modal, Tag } from 'antd';
import { Building2, Eye, FileStack, GitCompareArrows, ListTree, ScrollText } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { z } from 'zod';

export const Route = createFileRoute(
    '/admin-console/platform-content/compliance-audit-templates/details/$complianceAuditTemplateId',
)({
    staticData: {
        metadata: { title: 'File audit template' },
        ui: { widthVariant: 'default' },
    },
    params: {
        parse: ({ complianceAuditTemplateId }) => ({
            complianceAuditTemplateId: BigInt(complianceAuditTemplateId),
        }),
    },
    // `version` = read-only snapshot viewing (design D1). retainSearchParams is
    // load-bearing: the Tabs wrapper navigates with only `to`/`params`, which
    // would otherwise drop the param on every tab switch.
    validateSearch: zodValidator(z.object({ version: z.number().int().positive().optional() })),
    search: { middlewares: [retainSearchParams(['version'])] },
    beforeLoad({ context: { trpcUtils }, params, search }): ShareableRouteContext {
        // `version` must ride along: without it a snapshot view warms the wrong
        // cache key and the breadcrumb names the working version instead.
        void trpcUtils.admin.complianceAuditTemplate.getComplianceAuditTemplateVersion.ensureData({
            complianceAuditTemplateId: params.complianceAuditTemplateId,
            version: search.version,
        });

        return {
            breadcrumb: {
                async title() {
                    const template =
                        await trpcUtils.admin.complianceAuditTemplate.getComplianceAuditTemplateVersion.ensureData({
                            complianceAuditTemplateId: params.complianceAuditTemplateId,
                            version: search.version,
                        });
                    return template.workingVersion.name;
                },
            },
        };
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <TemplateDetails />
        </PageController>
    );
}

function TabLabel({ icon, label, count }: { icon: ReactNode; label: string; count?: number }) {
    return (
        <Space size="micro" className="items-center">
            {icon}
            {label}
            {/* Accent (primary) count chip — a static span, not an antd Badge: the
                Badge's built-in count/mount motion reads as a pulse on tab labels. */}
            {count !== undefined && (
                <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-500 px-1 font-medium text-2xs text-white leading-none">
                    {count}
                </span>
            )}
        </Space>
    );
}

function TemplateDetails() {
    const { complianceAuditTemplateId } = Route.useParams();
    const { version } = Route.useSearch();
    const { trpcUtils } = useTrpc();
    const titleCache = useTitleCacheStorage();
    const navigate = useNavigate();
    const { formatDate } = useFormatter();

    const getTemplateQuery = trpc.admin.complianceAuditTemplate.getComplianceAuditTemplateVersion.useQuery({
        complianceAuditTemplateId,
        version,
    });
    const updateRootMutation = trpc.admin.complianceAuditTemplate.updateComplianceAuditTemplate.useMutation();
    const updateVersionMutation = trpc.admin.complianceAuditTemplate.updateComplianceAuditTemplateVersion.useMutation();

    const template = getTemplateQuery.data;

    // Memoised — `useInit` re-initialises whenever the `data` reference changes, so an
    // inline object here would loop the store update on every render.
    const formData = useMemo(
        () =>
            template
                ? {
                      id: template.id,
                      type: template.type,
                      name: template.workingVersion.name,
                      description: template.workingVersion.description,
                      assignedOrgIds: template.AssignedOrgs.map(({ id }) => id),
                  }
                : undefined,
        [template],
    );

    complianceAuditTemplateFormStore.useInit({
        entityLabel: 'File audit template',
        data: formData,
        isLoading: getTemplateQuery.isLoading,
        // Snapshot views are read-only everywhere the store reaches (design D1).
        isReadOnly: (template?.isArchived ?? false) || (template?.isSnapshot ?? false),

        async onReload() {
            await getTemplateQuery.refetch();
        },

        async onSave(_, changedValues) {
            try {
                // Entity/Version split (design D7): org assignment goes to the root
                // procedure; everything else is versioned content.
                const { assignedOrgIds, id: _id, ...versionChanges } = changedValues;

                if (assignedOrgIds !== undefined) {
                    await updateRootMutation.mutateAsync({
                        complianceAuditTemplateId,
                        assignedOrgIds,
                    });
                }

                if (Object.keys(versionChanges).length) {
                    const result = await updateVersionMutation.mutateAsync({
                        complianceAuditTemplateId,
                        selectedVersion: version,
                        ...versionChanges,
                    });

                    if ('name' in versionChanges) {
                        titleCache.invalidate('breadcrumb:');
                    }

                    // Editing a published version silently forks a new draft (AUD-PUB-01) —
                    // surface it so the version tag change doesn't surprise the admin.
                    if (result.createdNewDraft) {
                        message.info(
                            `Draft version ${result.workingVersion.version} created from the published version`,
                        );
                    }
                }

                return { success: true };
            } catch (error) {
                return { success: false, error };
            } finally {
                // Invalidate on failure too: the root update (org assignment) may have
                // committed before the version update failed, and the cache must
                // reflect the persisted part.
                await trpcUtils.admin.complianceAuditTemplate.invalidate();
            }
        },
    });

    if (getTemplateQuery.isPending) {
        return <Loading />;
    }

    if (getTemplateQuery.error) {
        return (
            <InlineQueryError
                error={getTemplateQuery.error}
                onRetry={() => void getTemplateQuery.refetch()}
                title="Couldn't load this template"
            />
        );
    }

    // A HARDCODED template's content is the frozen playbook snapshot — there is
    // nothing to author, so the content tabs and version history are hidden and
    // the status + publish controls move onto the details card itself.
    const isHardcoded = template?.type === 'HARDCODED';

    return (
        <>
            {template && (
                <div className="flex flex-col gap-4">
                    {template.isSnapshot && (
                        <Alert
                            type="info"
                            showIcon
                            icon={<Eye size={15} />}
                            message={
                                <span>
                                    You are viewing <span className="font-medium font-mono">v{version}</span>
                                    {template.workingVersion.publishedAt
                                        ? `, published ${formatDate(template.workingVersion.publishedAt)}`
                                        : ''}{' '}
                                    — read-only. This is the exact content that version ran with; the current draft is
                                    untouched.
                                </span>
                            }
                            action={
                                <Button
                                    size="small"
                                    type="primary"
                                    onClick={() =>
                                        navigate({
                                            to: '/admin-console/platform-content/compliance-audit-templates/details/$complianceAuditTemplateId',
                                            params: { complianceAuditTemplateId },
                                            search: { version: undefined },
                                        })
                                    }
                                >
                                    Back to current version
                                </Button>
                            }
                        />
                    )}
                    <Card
                        size="small"
                        title="Template details"
                        classNames={{ body: 'space-y-4' }}
                        extra={
                            isHardcoded ? (
                                <div className="flex items-center gap-2">
                                    <StatusBadge
                                        variant="filled"
                                        module="complianceAuditTemplateVersion"
                                        status={template.workingVersion.status}
                                    />
                                    {!template.isSnapshot && (
                                        <PublishButton
                                            complianceAuditTemplateId={complianceAuditTemplateId}
                                            canPublish={
                                                template.workingVersion.status === 'DRAFT' && !template.isArchived
                                            }
                                        />
                                    )}
                                    <DeleteDraftMenu
                                        complianceAuditTemplateId={complianceAuditTemplateId}
                                        canDeleteDraft={
                                            template.workingVersion.status === 'DRAFT' &&
                                            template.latestPublishedVersion !== null &&
                                            !template.isArchived &&
                                            !template.isSnapshot
                                        }
                                    />
                                </div>
                            ) : undefined
                        }
                    >
                        <VTextInput
                            label="Name"
                            field="name"
                            formStore={complianceAuditTemplateFormStore}
                            rules={[{ required: true, message: 'Name is required' }]}
                        />
                        <VTextArea
                            label="Description"
                            field="description"
                            formStore={complianceAuditTemplateFormStore}
                        />
                        {/* Assignment is operational metadata, not versioned content — it
                            has no meaning against a snapshot, so it hides while viewing one. */}
                        {!template.isSnapshot && (
                            <VOrgSelect
                                field="assignedOrgIds"
                                formStore={complianceAuditTemplateFormStore}
                                editModalProps={{
                                    icon: <Building2 size={18} className="text-accent-600" />,
                                    title: 'Assign template to organisations',
                                    description: (
                                        <>
                                            Choose the organisations that should run{' '}
                                            <span className="font-medium text-foreground">
                                                {template.workingVersion.name}
                                            </span>
                                            . Assigned organisations get this template available in their audits.
                                        </>
                                    ),
                                }}
                            />
                        )}
                        <VReadOnly
                            label="Type"
                            field="type"
                            formStore={complianceAuditTemplateFormStore}
                            formatValueFn={(value) =>
                                value ? <ComplianceAuditTemplateTypeTag value={template.type} /> : null
                            }
                        />
                    </Card>
                    {!isHardcoded && (
                        <Tabs
                            items={[
                                {
                                    label: <TabLabel icon={<ListTree size={15} />} label="Structure" />,
                                    to: '/admin-console/platform-content/compliance-audit-templates/details/$complianceAuditTemplateId/structure',
                                },
                                {
                                    label: (
                                        <TabLabel
                                            icon={<ScrollText size={15} />}
                                            label="Principles"
                                            count={template.workingVersion.counts.principles}
                                        />
                                    ),
                                    to: '/admin-console/platform-content/compliance-audit-templates/details/$complianceAuditTemplateId/principles',
                                },
                                {
                                    label: (
                                        <TabLabel
                                            icon={<FileStack size={15} />}
                                            label="Evidence types"
                                            count={template.workingVersion.counts.evidenceTypes}
                                        />
                                    ),
                                    to: '/admin-console/platform-content/compliance-audit-templates/details/$complianceAuditTemplateId/evidence-types',
                                },
                                {
                                    // CUR-789 — what changed against the previous version
                                    // (draft vs latest published; vN vs its predecessor).
                                    label: <TabLabel icon={<GitCompareArrows size={15} />} label="Changes" />,
                                    to: '/admin-console/platform-content/compliance-audit-templates/details/$complianceAuditTemplateId/changes',
                                },
                            ]}
                            // PageLayout.Header portals don't render while a child tab route is the
                            // active match, so the working-version indicators and Publish action live
                            // on the Tabs bar (obligations-library details precedent).
                            rightAddon={
                                <div className="flex items-center gap-2">
                                    <VersionSelect
                                        complianceAuditTemplateId={complianceAuditTemplateId}
                                        selectedVersion={template.workingVersion.version}
                                        latestPublishedVersion={template.latestPublishedVersion}
                                    />
                                    {template.hasUnpublishedChanges && !template.isSnapshot ? (
                                        // Derived cross-version state, not an enum status — a
                                        // one-off Tag rather than a StatusBadge config entry.
                                        <Tag color="orange" variant="outlined" className="m-0">
                                            Draft – published version changed
                                        </Tag>
                                    ) : (
                                        <StatusBadge
                                            variant="filled"
                                            module="complianceAuditTemplateVersion"
                                            status={template.workingVersion.status}
                                        />
                                    )}
                                    {!template.isSnapshot && (
                                        <PublishButton
                                            complianceAuditTemplateId={complianceAuditTemplateId}
                                            canPublish={
                                                template.workingVersion.status === 'DRAFT' && !template.isArchived
                                            }
                                        />
                                    )}
                                    <DeleteDraftMenu
                                        complianceAuditTemplateId={complianceAuditTemplateId}
                                        // Mirror the server guard: a draft that has a published
                                        // version to fall back to, on a live (non-archived,
                                        // non-snapshot) template. Otherwise the menu is hidden.
                                        canDeleteDraft={
                                            template.workingVersion.status === 'DRAFT' &&
                                            template.latestPublishedVersion !== null &&
                                            !template.isArchived &&
                                            !template.isSnapshot
                                        }
                                    />
                                </div>
                            }
                        />
                    )}
                </div>
            )}
        </>
    );
}

function PublishButton({
    complianceAuditTemplateId,
    canPublish,
}: {
    complianceAuditTemplateId: bigint;
    canPublish: boolean;
}) {
    const { trpcUtils } = useTrpc();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [form] = Form.useForm<{ versionNotes?: string }>();

    const publishMutation = trpc.admin.complianceAuditTemplate.publishComplianceAuditTemplate.useMutation();

    const handlePublish = (values: { versionNotes?: string }) => {
        publishMutation.mutate(
            { complianceAuditTemplateId, versionNotes: values.versionNotes || undefined },
            {
                onSuccess(published) {
                    message.success(`Version ${published.version} published`);
                    setIsModalOpen(false);
                    form.resetFields();
                    void trpcUtils.admin.complianceAuditTemplate.invalidate();
                },
                onError(error) {
                    errorMessage.show(error);
                },
            },
        );
    };

    return (
        <>
            <Button type="primary" disabled={!canPublish} onClick={() => setIsModalOpen(true)}>
                Publish
            </Button>
            <Modal
                title="Publish this version?"
                open={isModalOpen}
                okText="Publish"
                confirmLoading={publishMutation.isPending}
                onOk={() => form.submit()}
                onCancel={() => setIsModalOpen(false)}
            >
                <p className="mb-3">
                    The version goes live for its assigned organisations. Further edits will create a new draft version.
                </p>
                <Form form={form} layout="vertical" onFinish={handlePublish} disabled={publishMutation.isPending}>
                    <Form.Item name="versionNotes" label="What changed (optional)">
                        <Input.TextArea rows={3} maxLength={8000} placeholder="Notes for the version history" />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
}

/**
 * The overflow menu next to Publish holding the single destructive action,
 * **Delete draft** (design D2). Discards the working draft and reverts to the
 * latest published version. Hidden entirely unless the action is available —
 * mirrors the server guard so the UI never offers a delete the server rejects.
 */
function DeleteDraftMenu({
    complianceAuditTemplateId,
    canDeleteDraft,
}: {
    complianceAuditTemplateId: bigint;
    canDeleteDraft: boolean;
}) {
    const { trpcUtils } = useTrpc();
    const deleteDraftMutation = trpc.admin.complianceAuditTemplate.deleteComplianceAuditTemplateDraft.useMutation();

    if (!canDeleteDraft) {
        return null;
    }

    return (
        <KebabMenu
            label="Template draft actions"
            items={[
                {
                    key: 'delete-draft',
                    label: 'Delete draft',
                    danger: true,
                    onClick: () =>
                        confirmDelete({
                            title: 'Delete draft?',
                            content:
                                'The working draft and its unpublished changes will be discarded, reverting to the latest published version. This cannot be undone.',
                            onConfirm: () =>
                                deleteDraftMutation.mutate(
                                    { complianceAuditTemplateId },
                                    {
                                        onSuccess() {
                                            message.success('Draft deleted — reverted to the published version');
                                            // Stay on the current tab; re-resolve the surface to
                                            // the published version (no redirect, design D2).
                                            void trpcUtils.admin.complianceAuditTemplate.invalidate();
                                        },
                                        onError(error) {
                                            errorMessage.show(error);
                                        },
                                    },
                                ),
                        }),
                },
            ]}
        />
    );
}
